import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { openRoutes, protectedRoutes } from './router';
import './assets/styles/Global/GlobalStyles.scss';
import { ToastContainer } from 'react-toastify';
import {
	CacheCleanup,
	EditMode,
	MaintenancePage,
	PageLoader,
	ScrollToTop,
	ErrorBoundary,
} from './components';
import { observer } from 'mobx-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { useTheme } from './context/ThemeContext';
import { adminStore, catalogStore } from './stores';
import constant from './utils/constant';
import Error404 from './pages/Error/Error404';
// import GoogleTagManager from './components/GoogleTagManager/GoogleTagManager';

export const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			networkMode: 'offlineFirst',
			refetchOnWindowFocus: false,
		},
		mutations: {
			networkMode: 'online',
		},
	},
});

function App() {
	const { theme } = useTheme();

	const [ThemeComponents, setThemeComponents] = useState({});
	const {
		editMode,
		configThemeData,
		themeData,
		//  metaDetails
	} = catalogStore;

	const isOpenRoute = openRoutes.some(route => {
		const routePattern = route.path.replace(/:[^\s/]+/g, '[^/]+');
		const regex = new RegExp(`^${routePattern}$`);
		return regex.test(window.location.pathname);
	});

	const isProtectedRoute = protectedRoutes.some(route => {
		const routePattern = route.path.replace(/:[^\s/]+/g, '[^/]+');
		const regex = new RegExp(`^${routePattern}$`);
		return regex.test(window.location.pathname);
	});

	const usedThemeData = useMemo(() => {
		if (themeData?.pages?.length > 0) {
			return themeData;
		}

		return configThemeData;
	}, [configThemeData, themeData]);

	const color = useMemo(() => {
		return usedThemeData?.generalSettings?.data?.find(
			color => color?.id === 'color'
		);
	}, [usedThemeData?.generalSettings?.data]);

	useEffect(() => {
		const loadTheme = async () => {
			const themeConfig = await import(`./themes/${theme}/theme.config.js`);
			const components = themeConfig.default.components;

			const colors = themeConfig?.default?.colors;

			if (color?.defaultContent?.primary) {
				document.documentElement.style.setProperty(
					'--theme-primary-color',
					color?.defaultContent?.primary
				);
			} else if (colors?.primary) {
				document.documentElement.style.setProperty(
					'--theme-primary-color',
					colors?.primary
				);
			}

			if (color?.defaultContent?.secondary) {
				document.documentElement.style.setProperty(
					'--theme-secondary-color',
					color?.defaultContent?.secondary
				);
			} else if (colors?.secondary) {
				document.documentElement.style.setProperty(
					'--theme-secondary-color',
					colors?.secondary
				);
			}
			if (color?.defaultContent?.tertiary) {
				document.documentElement.style.setProperty(
					'--theme-tertiary-color',
					color?.defaultContent?.tertiary
				);
			} else if (colors?.tertiary) {
				document.documentElement.style.setProperty(
					'--theme-tertiary-color',
					colors?.tertiary
				);
			}
			if (color?.defaultContent?.backdrop) {
				document.documentElement.style.setProperty(
					'--theme-backdrop-color',
					color?.defaultContent?.backdrop
				);
			} else if (colors?.backdrop) {
				document.documentElement.style.setProperty(
					'--theme-backdrop-color',
					colors?.backdrop
				);
			}
			setThemeComponents(components);
		};
		loadTheme();
	}, [
		color?.defaultContent?.primary,
		color?.defaultContent?.secondary,
		color?.defaultContent?.tertiary,
		color?.defaultContent?.backdrop,
		theme,
	]);

	const { webConfig } = catalogStore;
	const { loggedIn } = adminStore;

	const maintenanceMode = webConfig.maintenanceMode;
	const isAdminRoute = window.location.pathname.startsWith('/admin');
	const shouldShowMaintenancePage = maintenanceMode && !isAdminRoute;

	return (
		<QueryClientProvider client={queryClient}>
			<ErrorBoundary silent>
				<CacheCleanup />
			</ErrorBoundary>
			<BrowserRouter>
				{shouldShowMaintenancePage ? (
					<MaintenancePage />
				) : (
					<Suspense fallback={<PageLoader />}>
						<ScrollToTop />
						{editMode && isOpenRoute ? <EditMode /> : ''}
						<Routes>
							{openRoutes.map(({ path, component: ComponentName, element }) => {
								if (element) {
									return <Route key={path} path={path} element={element} />;
								} else {
									const Component = ThemeComponents[ComponentName];

									return (
										<Route
											key={path}
											path={path}
											element={Component ? <Component /> : <PageLoader />}
										/>
									);
								}
							})}
							{loggedIn
								? protectedRoutes.map(route => (
										<Route
											key={route.path}
											path={route.path}
											element={route.element}
										/>
									))
								: null}
							<Route
								path="*"
								element={
									!loggedIn && isProtectedRoute ? (
										<Navigate to={constant.Routes.Admin.Login} replace />
									) : (
										<Error404 />
									)
								}
							/>
						</Routes>
						<ToastContainer closeButton={false} position="bottom-right" />
					</Suspense>
				)}
			</BrowserRouter>
		</QueryClientProvider>
	);
}

export default observer(App);
